import { ButtonRules, StyledButton } from '@canopysecurity/component-library';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DeployOtaToDevice } from '../Components/device-details/DeployOtaToDevice';
import { DeviceDeploymentDetails } from '../Components/device-details/DeviceDeploymentDetails';
import { DeviceInfo } from '../Components/device-details/DeviceInfo';
import { DeviceSearch } from '../Components/device-details/DeviceSearch';
import { DeviceTags } from '../Components/device-details/DeviceTags';
import WelcomeBannerHome from '../Components/home/WelcomeBannerHome';
import { OnSubmitModal } from './OnSubmitModal';
import { getDeployments } from '../services/DeploymentService';

import {
  deployToDevice,
  getDeploymentInfoForDevice,
  getDeviceTags,
} from '../services/DevicesService';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiClient } from '../hooks/useApiClient';

interface DeviceDetailsProps {
  canopyDeviceId: string;
  batteryLevel: number;
  deviceReportedVersion: string;
  lastConnected: string;
  lifecycleState: string;
  pendingOta: {
    status: string;
    version: string;
  };
  vehicle: {
    isInMotion: boolean | null;
    updatedAt: string;
  };
}

export default function DeviceDetails() {
  const [searchParams] = useSearchParams();
  const searchParamsSN = searchParams.get('serialNumber') || '';
  const [serialNumber, setSerialNumber] = useState(searchParamsSN);
  const [skipQuery, setSkipQuery] = useState<boolean>(!serialNumber);
  const [deviceDetails, setDeviceDetails] = useState<DeviceDetailsProps>();
  const [modalState, setModalState] = useState({
    isVisible: false,
    title: '',
    message: '',
  });
  const [updatedDeviceDeployments, setUpdatedDeviceDeployments] =
    useState<any[]>();
  const [updatedDeployments, setUpdatedDeployments] = useState<any[]>([]);
  const [selectedDeployment, setSelectedDeployment] = useState<string>('');
  const { token } = useApiClient();

  const {
    data,
    isLoading,
    error: deploymentInfoError,
    refetch,
  } = useQuery({
    queryKey: ['deploymentInfo'],
    queryFn: () => getDeploymentInfoForDevice(serialNumber),
    enabled: !!serialNumber && !!token,
  });

  const { data: deploymentData, isLoading: deploymentLoading } = useQuery({
    queryKey: ['deployments'],
    queryFn: () => getDeployments('active'),
    enabled: !!token,
  });

  const { mutate, isPending: deployToDeviceLoading } = useMutation({
    mutationFn: deployToDevice,
    onSuccess: () => {
      setModalState({
        isVisible: true,
        title: 'Success',
        message: 'OTA successfully requested',
      });
    },
    onError: (error) => {
      setModalState({
        isVisible: true,
        title: 'Error',
        message: error.message,
      });
    },
  });

  // need to invalidate cache for device tags query when serial number changes and when tag is disassociated
  const {
    data: deviceTagData,
    isLoading: deviceTagLoading,
    error: deviceTagError,
    refetch: refetchDeviceTags,
  } = useQuery({
    queryKey: ['deviceTags'],
    queryFn: () => getDeviceTags({ serialNumber, limit: 150 }),
    enabled: !!serialNumber && !!token,
  });

  const onDeployToDevice = async () => {
    if (!selectedDeployment || !deviceDetails?.canopyDeviceId) {
      return;
    }
    mutate({
      canopyDeviceId: deviceDetails.canopyDeviceId,
      deploymentId: selectedDeployment,
      serialNumber: serialNumber,
    });
  };

  const handleRefresh = async () => {
    await refetch();
  };
  const handleCloseModal = async () => {
    setModalState({ isVisible: false, title: '', message: '' });
    await refetch();
  };

  useMemo(() => {
    setDeviceDetails({
      canopyDeviceId: data?.canopyDeviceId ?? '',
      batteryLevel: data?.batteryLevel ?? 0,
      deviceReportedVersion: data?.deviceReportedVersion ?? '?',
      lastConnected: data?.lastConnected ?? '?',
      lifecycleState: data?.lifecycleState ?? '',
      pendingOta: {
        status: data?.pendingOta?.status ?? '',
        version: data?.pendingOta?.version ?? '',
      },
      vehicle: {
        isInMotion: data?.vehicle?.isInMotion ?? null,
        updatedAt: data?.vehicle?.updatedAt ?? '',
      },
    });
    const deviceDeployments = data?.deployments;
    if (deviceDeployments && deviceDeployments.length) {
      const deviceDeploymentsWorking = deviceDeployments?.map((deployment) => {
        return {
          version: deployment.version,
          jobId: deployment.jobId,
          status: deployment.status,
          states: deployment.states && {
            download: deployment.states?.download?.map((state: any) => {
              return { status: state?.status, updatedAt: state?.updatedAt };
            }),
            ...(deployment.states &&
              deployment.states.install && {
                install: deployment.states?.install.map((state: any) => {
                  return { status: state?.status, updatedAt: state?.updatedAt };
                }),
              }),
          },
        };
      });
      if (deviceDeploymentsWorking && deviceDeploymentsWorking.length) {
        setUpdatedDeviceDeployments(deviceDeploymentsWorking);
      }
    }
  }, [data]);
  useMemo(() => {
    setUpdatedDeployments(deploymentData ?? []);
  }, [deploymentData]);

  return (
    <>
      <WelcomeBannerHome subtitle="Have fun looking up Device Details and stuff" />
      <div>
        <DeviceSearch
          serialNumber={serialNumber}
          setSerialNumber={setSerialNumber}
          loading={isLoading}
          setSkipQuery={setSkipQuery}
        />
      </div>
      <div>
        {skipQuery ? (
          <span style={{ marginLeft: '20%' }}>No Device Selected</span>
        ) : (
          <>
            {deploymentInfoError ? (
              <div style={{ display: 'flex' }}>
                <div style={{ flexBasis: '20%' }}></div>
                <div
                  style={{
                    flexBasis: '80%',
                    paddingLeft: '20px',
                    color: 'red',
                    fontWeight: 'bold',
                  }}
                >
                  {deploymentInfoError.message}
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex' }}>
                  <div style={{ flexBasis: '40%' }}></div>
                  <div style={{ flexBasis: '20%' }}>
                    <StyledButton
                      onPress={handleRefresh}
                      buttonRules={ButtonRules.primaryA}
                    >
                      Refresh
                    </StyledButton>
                  </div>
                  <div style={{ flexBasis: '40%' }}></div>
                </div>
                <div style={{ display: 'flex' }}>
                  <div style={{ flexBasis: '50%' }}>
                    <DeviceDeploymentDetails
                      loading={isLoading}
                      deployments={updatedDeviceDeployments}
                      canopyDeviceId={deviceDetails?.canopyDeviceId}
                      refetch={refetch}
                    />
                  </div>
                  <div style={{ flexBasis: '50%' }}>
                    <DeviceInfo
                      loading={isLoading}
                      deviceDetails={deviceDetails}
                    />
                    <DeployOtaToDevice
                      deploymentLoading={deploymentLoading}
                      deployToDeviceLoading={deployToDeviceLoading}
                      selectedDeployment={selectedDeployment}
                      setSelectedDeployment={setSelectedDeployment}
                      updatedDeployments={updatedDeployments}
                      onDeployToDevice={onDeployToDevice}
                    />
                    <DeviceTags
                      loading={deviceTagLoading}
                      data={deviceTagData}
                      error={deviceTagError}
                      serialNumber={serialNumber}
                      refetch={refetchDeviceTags}
                    />
                  </div>
                </div>
                <OnSubmitModal
                  isVisible={modalState.isVisible}
                  onClose={handleCloseModal}
                  title={modalState.title}
                  message={modalState.message}
                />
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}
